import {
  AfterViewInit,
  Component,
  ElementRef,
  EmbeddedViewRef,
  Inject,
  Input,
  NgZone,
  PLATFORM_ID,
  TransferState,
  ViewContainerRef,
} from '@angular/core';

import {
  DxTemplateHost,
  DxTreeViewComponent,
  IterableDifferHelper,
  NestedOptionHost,
  WatcherHelper,
} from 'devextreme-angular';
import { DataSource } from 'devextreme/common/data';
import { treeIndicatorPosition } from '../../../base/src/abcTree/abcTree';
import { _AbcTooltipComponent } from '../abc-tooltip/abc-tooltip.component';
import { _AbcTreeNode } from './abc-tree';

@Component({
  selector: 'abc-tree',
  template: ``,
  styleUrls: ['./abc-tree.component.scss'],
  providers: [DxTemplateHost, WatcherHelper, IterableDifferHelper, NestedOptionHost],
})
export class _AbcTreeComponent extends DxTreeViewComponent implements AfterViewInit {
  private _data: any[];
  private nodesByIdExpr: Map<string, _AbcTreeNode> = new Map();
  private nodesById: Map<string, _AbcTreeNode> = new Map();
  private _idExpr: string;
  private tooltipDiv: HTMLDivElement;
  private elementRef: ElementRef;
  private tooltipComponent;

  /**
   * _forceTooltip : Indicates whether the tooltip content should be extracted from the 'data-value' attribute.
   * In the template, you can pass your tooltip content using the specified attribute:
   *
   * <span [attr.data-value]="customTooltipContent">...</span>
   */
  private _forceTooltip: boolean = false;
  @Input() hideTooltip: boolean = false;
  @Input()
  set forceTooltip(value: boolean) {
    this._forceTooltip = value;
  }
  get forceTooltip() {
    return this._forceTooltip;
  }

  @Input()
  get idExpr(): string {
    return this._idExpr;
  }
  set idExpr(value: string) {
    this._idExpr = value;
  }

  @Input()
  get data(): any[] {
    return this._data;
  }
  set data(data: any[]) {
    this._data = data;
    this.dataSource = this.getDataSource(data);
  }

  @Input()
  get indicatorPosition(): treeIndicatorPosition {
    return this.rtlEnabled ? 'right' : 'left';
  }
  set indicatorPosition(data: treeIndicatorPosition) {
    this.rtlEnabled = data === 'right' ? true : false;
  }

  private _createTreeNodeMethod;
  @Input()
  get createTreeNodeMethod(): any {
    return this._createTreeNodeMethod;
  }
  set createTreeNodeMethod(val) {
    this._createTreeNodeMethod = val;
  }

  private _getChildrenMethod;
  @Input()
  get getChildrenMethod(): any {
    return this._getChildrenMethod;
  }
  set getChildrenMethod(val) {
    this._getChildrenMethod = val;
  }

  constructor(
    elementRef: ElementRef,
    ngZone: NgZone,
    templateHost: DxTemplateHost,
    _watcherHelp: WatcherHelper,
    _idh: IterableDifferHelper,
    optionHost: NestedOptionHost,
    transferState: TransferState,
    @Inject(PLATFORM_ID) platformId: any,
    private viewContainerRef: ViewContainerRef
  ) {
    super(elementRef, ngZone, templateHost, _watcherHelp, _idh, optionHost, transferState, platformId);

    this.displayExpr = 'caption';
    this.itemsExpr = 'children';
    this.elementRef = elementRef;

    this.onContentReady.subscribe((e) => {
      if (this.tooltipComponent) {
        this.tooltipDiv.appendChild(this.tooltipComponent.domElement);
        const tooltip = <_AbcTooltipComponent>this.tooltipComponent.componentRef.instance;
        const elements = e.component.element().querySelectorAll('.dx-treeview-item') as NodeListOf<HTMLElement>;
        elements.forEach((el) => {
          const children: HTMLElement = el.children[0] as HTMLElement;
          if (children.offsetWidth < el.children[0].scrollWidth || this.forceTooltip) {
            el.onmouseenter = (event) => {
              tooltip.contentTemplate =
                el?.children[0]?.children[0]?.children[0]?.getAttribute('data-value') ?? el.innerText;
              tooltip.target = event.target as unknown as string;
              tooltip.visible = true;
            };
            el.onmouseleave = () => {
              tooltip.visible = false;
            };
          }
        });
      }
    });

    this.noDataText = '';
  }

  override ngAfterViewInit() {
    if (!this.hideTooltip) {
      this.tooltipComponent = this.createAbcTooltipComponent(this.elementRef);
    }
    this.instance.endUpdate();
  }

  private getDataSource(data: any[]): DataSource<any, any> {
    let dataSource: DataSource;

    if (data) {
      if (this.createTreeNodeMethod && this.getChildrenMethod) {
        dataSource = this.createDataSource(data);
      } else {
        dataSource = new DataSource(data);

        this.indexDataSource(data);
      }
    }
    return dataSource;
  }

  private indexDataSource(data: _AbcTreeNode[]) {
    data?.forEach((node: _AbcTreeNode) => {
      this.nodesById.set(node.id, node);
      if (this.idExpr) {
        this.nodesByIdExpr.set(node[this.idExpr], node);
      }

      this.indexDataSource(node.children);
    });
  }

  private createDataSource(data: unknown): DataSource {
    let dataSource: DataSource;

    if (data && this.createTreeNodeMethod && this.getChildrenMethod) {
      const newData: Array<_AbcTreeNode> = this.createAbcTreeNodes(this.data, undefined);
      dataSource = new DataSource(newData);
    }

    return dataSource;
  }
  private createAbcTreeNodes(arrData: any, parentNode: _AbcTreeNode): _AbcTreeNode[] {
    const abcTreeNodes: _AbcTreeNode[] = [];
    let index = 0;
    for (const element of arrData) {
      let id: string = '';
      const treeNode: _AbcTreeNode = this.createTreeNodeMethod(element);
      treeNode.data = element;
      if (parentNode) {
        id = parentNode.id + '_';
        treeNode.parentNode = parentNode;
        treeNode.parentId = parentNode.id;
      }
      id += this.idExpr ? element[this.idExpr] : index;
      treeNode.id = id;
      treeNode.children = this.getChildrenMethod(element)
        ? this.createAbcTreeNodes(this.getChildrenMethod(element), treeNode)
        : null;

      abcTreeNodes.push(treeNode);
      this.nodesById.set(treeNode.id, treeNode);
      if (this.idExpr) {
        this.nodesByIdExpr.set(element[this.idExpr], treeNode);
      }
      index++;
    }
    return abcTreeNodes;
  }

  public addNode(node: _AbcTreeNode) {
    this.instance.beginUpdate();
    const dataSource: DataSource = this.instance.getDataSource();

    const parentNode = this.getNodeById(node.parentId);
    if (parentNode) {
      if (!parentNode.children.find((childNode) => childNode.id === node.id)) {
        node.parentId = parentNode.id;
        node.parentNode = parentNode;
        parentNode.children.push(node);
      }
    } else {
      dataSource.items().push(node);
    }

    this.addNodeToIndex(node);

    //BOR: Not happy with this
    this.dataSource = dataSource;
    this.instance.endUpdate();
  }

  private addNodeToIndex(node: _AbcTreeNode) {
    this.nodesById.set(node.id, node);

    if (this.idExpr) {
      this.nodesByIdExpr.set(node.data[this.idExpr], node);
    }

    node.children.forEach((childNode: _AbcTreeNode) => {
      this.addNodeToIndex(childNode);
    });
  }

  public removeNode(nodeToRemove: _AbcTreeNode) {
    this.instance.beginUpdate();
    const dataSource: DataSource = this.instance.getDataSource();

    if (nodeToRemove.parentId) {
      const parentNode = this.getNodeById(nodeToRemove.parentId);

      const indexToRemove = parentNode.children.findIndex((obj) => obj.id === nodeToRemove.id);
      parentNode.children.splice(indexToRemove, 1);
    } else {
      const indexToRemove = dataSource.items().findIndex((obj) => obj.id === nodeToRemove.id);
      dataSource.items().splice(indexToRemove, 1);
    }

    nodeToRemove.children?.forEach((child) => {
      this.nodesById.delete(child.id);
      this.nodesByIdExpr.delete(child.id);
    });

    this.nodesById.delete(nodeToRemove.id);
    this.nodesByIdExpr.delete(nodeToRemove.id);

    this.dataSource = dataSource;
    this.instance.endUpdate();
  }

  public getNodeById(id: string): _AbcTreeNode | undefined {
    const node = this.nodesById.get(id);
    return node;
  }

  public getNodeByIdExpr(idExpr: string): _AbcTreeNode | undefined {
    const node = this.nodesByIdExpr.get(idExpr);
    return node;
  }

  private createAbcTooltipComponent(elementRef) {
    const nativeElement = <HTMLDivElement>elementRef.nativeElement;
    this.tooltipDiv = document.createElement('div');
    nativeElement.parentNode?.insertBefore(this.tooltipDiv, nativeElement.nextSibling);
    const componentRef = this.viewContainerRef.createComponent(_AbcTooltipComponent);
    const domElement = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    return { componentRef, domElement };
  }
}
